import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';

import { Button, Coachmark, Icon } from '@bookora/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import type { GuidanceStateResponse } from '@/api/generated/model';
import { queryKeys } from '@/api/query-keys';
import { useSessionStore } from '@/auth/token-store';
import { toast } from '@/lib/feedback';

import { useGuidance, useRecordGuidance } from './api';
import { useStepContext } from './context';
import { useTourFeatures } from './features';
import { useTourFinish } from './finish';
import { mayAutoOfferNow, mayOfferHelpNow } from './offer-context';
import { isEditingNow, useCollapseWhenIgnored, waitForQuiet } from './quiet';
import {
  resolveSteps,
  tourAllowed,
  trimToMax,
  usableStepCount,
  type ResolvedStep,
} from './resolve-steps';
import { RUNTIME_COPY } from './runtime-copy';
import { noteHelpShown, noteProactiveShown, readHostOpens, writeHelpBackoff } from './storage';
import { reportProgress, takeInlineTrip, useStruggleStore } from './struggle/store';
import {
  HELP_MAX_WAIT_MS,
  HELP_QUIET_MS,
  INLINE_OFFER_MIN_OPENS,
  OFFER_MAX_WAIT_MS,
  QUIET_MS,
} from './timing';
import { useTourStore, type ActiveTour } from './tour-store';
import { waitForAnchors } from './wait-anchors';
import { COPY } from './tours/copy';
import { TOURS } from './tours/registry';
import type { InlineHostKey } from './tours/types';

/**
 * The inline tour's slot inside its host form (the first child of the body).
 *
 * The session form is a focus-locked antd Drawer, and a panel portaled to
 * `body` is unreachable from inside it — so this tour is a card in the
 * form's own flow (`Coachmark mode="inline"`), and its anchors are looked up
 * inside the host dialog only. This slot, not the floating runner, records
 * the inline tour's events.
 *
 *  · At rest: a one-line «راهنمای این فرم» row.
 *  · From the second open on this device: one quiet offer beside that row;
 *    the first focus in a form field collapses it.
 *  · After the form was opened and closed twice without being used (critic
 *    C6's abandon signal), the NEXT open offers help instead — at the step
 *    the person was in, even for a tour they already finished. Its OFFERED
 *    and its answer carry source `struggle`; a «نه، ممنون» backs off this
 *    form for two weeks and never pauses the other offers.
 *  · An armed tour (hub «آماده کردن راهنما», a tip, a deep link) starts on the next
 *    open once the form's data has loaded.
 *  · The drawer closing mid-tour records `INTERRUPTED (host-closed)`.
 */
export function InlineTourSlot({ tour: key }: { tour: InlineHostKey }): JSX.Element | null {
  const tour = TOURS[key];
  const ctx = useStepContext();
  const features = useTourFeatures();
  const guidance = useGuidance();
  const record = useRecordGuidance();
  const queryClient = useQueryClient();
  const clinicId = useSessionStore((s) => s.activeClinicId);
  const userId = useSessionStore((s) => s.user?.id ?? null);
  const host = useTourStore((s) => (s.inlineHost?.key === key ? s.inlineHost : null));
  const active = useTourStore((s) => (s.active?.key === key ? s.active : null));
  const armed = useTourStore((s) => s.armed);
  const offerOpen = useTourStore((s) => s.offer?.key === key);
  const help = useTourStore((s) => (s.offer?.key === key ? (s.offer.help ?? null) : null));
  const inlineTrip = useStruggleStore((s) => s.inline[key] ?? null);
  const offerId = `bk-tour-offer-${key}`;
  const start = useTourStore((s) => s.start);
  const takeArmed = useTourStore((s) => s.takeArmed);
  const setOffer = useTourStore((s) => s.setOffer);
  const slotRef = useRef<HTMLDivElement>(null);
  const rowButton = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null);

  const allowed = tourAllowed(tour, ctx.permissions, features);
  const steps = useMemo(() => resolveSteps(tour, ctx, { trim: false }), [tour, ctx]);
  const visible = allowed && steps.length >= tour.minSteps;
  const helpStep = help?.startAtStepId
    ? steps.find((step) => step.id === help.startAtStepId)
    : undefined;
  const helpBody = helpStep
    ? RUNTIME_COPY.help.stepBody(helpStep.title)
    : RUNTIME_COPY.help.inlineBody;
  const hostOpen = Boolean(host?.open);
  const hostReady = Boolean(host?.open && host.ready);

  const hostElement = useCallback((): HTMLElement | null => {
    const slot = slotRef.current;
    return (slot?.closest('[role="dialog"]') as HTMLElement | null) ?? slot?.parentElement ?? null;
  }, []);

  // Armed: start on the open that follows.
  useEffect(() => {
    if (!hostReady || !allowed || armed?.key !== key) return;
    const taken = takeArmed(key);
    if (taken) start(key, { source: taken.source, startAtStepId: taken.startAtStepId });
  }, [hostReady, allowed, armed, key, takeArmed, start]);

  // A help offer, on the open after two abandoned ones (critic C6). It goes
  // first: someone who keeps closing the form unused needs it more than a
  // first-visit hint.
  const offeredThisOpen = useRef(false);
  useEffect(() => {
    if (!hostOpen) offeredThisOpen.current = false;
  }, [hostOpen]);
  useEffect(() => {
    if (!hostReady || !visible || active || offeredThisOpen.current || !inlineTrip) return;
    const root = hostElement();
    const context = inlineTrip.context;
    const offerable = (data: GuidanceStateResponse | undefined): boolean =>
      mayOfferHelpNow(data, context, { ownHost: root });
    if (!offerable(guidance.data)) return;
    const controller = new AbortController();
    void (async () => {
      const quiet = await waitForQuiet(HELP_QUIET_MS, HELP_MAX_WAIT_MS, controller.signal, {
        notEditing: true,
      });
      if (!quiet || controller.signal.aborted) return;
      if (!offerable(queryClient.getQueryData(queryKeys.guidance.state(clinicId)))) return;
      const trip = takeInlineTrip(key);
      if (!trip) return;
      const startAtStepId = trip.anchor
        ? steps.find((step) => step.anchor === trip.anchor)?.id
        : undefined;
      offeredThisOpen.current = true;
      const now = new Date();
      noteHelpShown(now);
      writeHelpBackoff(userId, clinicId, context, { shownAt: now.toISOString() });
      setOffer(key, { context, ...(startAtStepId ? { startAtStepId } : {}) });
      record({ type: 'OFFERED', kind: 'TOUR', key, version: tour.version, source: 'struggle' });
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostReady, visible, active, inlineTrip]);

  // The auto-offer, from the second open on this device.
  useEffect(() => {
    if (!hostReady || !visible || active || offeredThisOpen.current || inlineTrip) return;
    if (readHostOpens(userId, clinicId, key) < INLINE_OFFER_MIN_OPENS) return;
    const root = hostElement() ?? undefined;
    const count = (): number => usableStepCount(steps, root, tour.maxSteps);
    // The drawer itself is a modal dialog; inside it, only a popup opened
    // from the form would count as "something on top".
    const offerable = (data: GuidanceStateResponse | undefined): boolean =>
      mayAutoOfferNow(tour, data, count(), { ownHost: root ?? null });
    if (isEditingNow() || !offerable(guidance.data)) return;
    const controller = new AbortController();
    void (async () => {
      const quiet = await waitForQuiet(QUIET_MS, OFFER_MAX_WAIT_MS, controller.signal, {
        notEditing: true,
      });
      if (!quiet || controller.signal.aborted) return;
      if (isEditingNow()) return;
      if (!offerable(queryClient.getQueryData(queryKeys.guidance.state(clinicId)))) return;
      offeredThisOpen.current = true;
      noteProactiveShown(new Date());
      setOffer(key);
      record({ type: 'OFFERED', kind: 'TOUR', key, version: tour.version, source: 'auto' });
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostReady, visible, active]);

  // The first focus in a form field collapses the offer (the form wins).
  useEffect(() => {
    if (!offerOpen) return;
    const root = hostElement();
    if (!root) return;
    const onFocusIn = (event: FocusEvent): void => {
      if (slotRef.current?.contains(event.target as Node)) return;
      setOffer(null);
    };
    root.addEventListener('focusin', onFocusIn);
    return () => root.removeEventListener('focusin', onFocusIn);
  }, [offerOpen, hostElement, setOffer]);
  useCollapseWhenIgnored(offerOpen, offerId, () => setOffer(null));

  // The host went away with the offer up: nothing to say about it.
  useEffect(() => {
    if (!hostOpen && offerOpen) setOffer(null);
  }, [hostOpen, offerOpen, setOffer]);

  if (!visible) return null;

  if (active) {
    return (
      <div ref={slotRef} className="bk-inline-tour-slot">
        <InlineRun
          key={active.runId}
          active={active}
          hostOpen={hostOpen}
          hostElement={hostElement}
        />
      </div>
    );
  }

  const launch = (): void => {
    setOffer(null);
    start(key, { source: 'chip', returnFocusTo: rowButton.current });
  };

  return (
    <div ref={slotRef} className="bk-inline-tour-slot">
      <span className="bk-coachmark-anchor">
        <Button
          ref={rowButton}
          type="link"
          size="small"
          icon={<Icon name="help" size={16} />}
          onClick={launch}
          {...(offerOpen ? { 'aria-expanded': true, 'aria-controls': offerId } : {})}
        >
          {COPY.chip.inlineRow}
        </Button>
        {offerOpen ? (
          <Coachmark
            open
            mode="offer"
            id={offerId}
            step={{
              id: help ? 'help' : 'offer',
              title: help ? RUNTIME_COPY.help.title : COPY.offer.title(tour.title),
              body: (
                <>
                  <p>{help ? helpBody : tour.offer?.body}</p>
                  <p className="bk-tour-offer__footnote">{COPY.offer.footnote}</p>
                </>
              ),
              target: null,
            }}
            index={0}
            total={1}
            primaryLabel={help ? RUNTIME_COPY.help.primary : COPY.offer.primary}
            secondaryLabel={help ? RUNTIME_COPY.help.secondary : COPY.offer.secondary}
            onAccept={() => {
              setOffer(null);
              start(
                key,
                help
                  ? { source: 'struggle', startAtStepId: help.startAtStepId }
                  : { source: 'offer' },
              );
            }}
            onDecline={() => {
              setOffer(null);
              if (help) {
                writeHelpBackoff(userId, clinicId, help.context, {
                  declinedAt: new Date().toISOString(),
                });
              }
              record({
                type: 'OFFER_DECLINED',
                kind: 'TOUR',
                key,
                version: tour.version,
                source: help ? 'struggle' : 'offer',
              });
            }}
            onDismiss={() => setOffer(null)}
            onNext={() => undefined}
            onPrev={() => undefined}
            onFinish={() => undefined}
          />
        ) : null}
      </span>
      <span className="bk-visually-hidden" aria-live="polite">
        {offerOpen ? (help ? RUNTIME_COPY.help.live : COPY.offer.live) : ''}
      </span>
    </div>
  );
}

interface LiveStep {
  step: ResolvedStep;
  el: HTMLElement;
}

function InlineRun({
  active,
  hostOpen,
  hostElement,
}: {
  active: ActiveTour;
  hostOpen: boolean;
  hostElement: () => HTMLElement | null;
}): JSX.Element | null {
  const tour = TOURS[active.key];
  const ctx = useStepContext();
  const record = useRecordGuidance();
  const navigate = useNavigate();
  const finishAction = useTourFinish(tour, ctx);
  const end = useTourStore((s) => s.end);
  const [steps, setSteps] = useState<LiveStep[] | null>(null);
  const [index, setIndex] = useState(0);
  const endedRef = useRef(false);
  const stateRef = useRef({ index, steps });
  stateRef.current = { index, steps };

  const emit = useCallback(
    (
      type: 'STARTED' | 'STEP_VIEWED' | 'STEP_SKIPPED' | 'COMPLETED' | 'DISMISSED' | 'INTERRUPTED',
      extra: {
        step?: number;
        stepId?: string;
        total?: number;
        reason?: 'anchor_missing' | 'host-closed';
        durationMs?: number;
      } = {},
    ) => {
      record({
        type,
        kind: 'TOUR',
        key: tour.key,
        version: tour.version,
        source: active.source,
        ...extra,
      });
    },
    [record, tour.key, tour.version, active.source],
  );

  const finish = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    end();
  }, [end]);

  useEffect(() => {
    const controller = new AbortController();
    const resolved = resolveSteps(tour, ctx, { trim: false });
    const root = hostElement() ?? undefined;
    void (async () => {
      const found = await waitForAnchors(
        resolved.map((s) => s.anchor),
        { signal: controller.signal, root },
      );
      if (controller.signal.aborted || endedRef.current) return;
      const present: LiveStep[] = [];
      resolved.forEach((step, i) => {
        const el = found[i];
        if (el) present.push({ step, el });
        else emit('STEP_SKIPPED', { stepId: step.id, reason: 'anchor_missing' });
      });
      // «≤ 5» counts the steps shown (critic C10): trim the live ones.
      const live = trimToMax(tour, present, active.startAtStepId);
      if (live.length === 0) {
        toast.info(COPY.toast.unavailable);
        finish();
        return;
      }
      const at = active.startAtStepId
        ? live.findIndex((s) => s.step.id === active.startAtStepId)
        : -1;
      const first =
        at >= 0
          ? at
          : active.resumeIndex !== undefined
            ? Math.min(Math.max(0, active.resumeIndex), live.length - 1)
            : 0;
      setSteps(live);
      setIndex(first);
      emit('STARTED', { step: first, stepId: live[first]?.step.id, total: live.length });
    })();
    return () => controller.abort();
    // Resolved once per run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The drawer closed (or unmounted) mid-tour. A clinic switch clears the
  // run first, and then nothing is recorded.
  useEffect(() => {
    if (hostOpen || endedRef.current || !stateRef.current.steps) return;
    emit('INTERRUPTED', { step: stateRef.current.index, reason: 'host-closed' });
    finish();
  }, [hostOpen, emit, finish]);
  useEffect(
    () => () => {
      if (endedRef.current || !stateRef.current.steps) return;
      if (useTourStore.getState().active?.runId !== active.runId) return;
      endedRef.current = true;
      emit('INTERRUPTED', { step: stateRef.current.index, reason: 'host-closed' });
      useTourStore.getState().end();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (!steps) return null;
  const current = steps[index];
  const total = steps.length;

  return (
    <Coachmark
      open
      mode="inline"
      ariaLabel={COPY.chip.inlineRegion}
      step={{
        id: current.step.id,
        title: current.step.title,
        body: current.step.body,
        target: current.el,
      }}
      index={index}
      total={total}
      labels={finishAction ? { finish: finishAction.label } : undefined}
      onNext={() => {
        const next = Math.min(index + 1, total - 1);
        emit('STEP_VIEWED', { step: next, stepId: steps[next]?.step.id, total });
        setIndex(next);
      }}
      onPrev={() => {
        const prev = Math.max(0, index - 1);
        emit('STEP_VIEWED', { step: prev, stepId: steps[prev]?.step.id, total });
        setIndex(prev);
      }}
      onFinish={() => {
        emit('COMPLETED', {
          step: index,
          stepId: current.step.id,
          total,
          durationMs: Math.min(3_600_000, Math.max(0, Date.now() - active.startedAt)),
        });
        reportProgress();
        finish();
        if (finishAction) void navigate(finishAction.to);
      }}
      onDismiss={() => {
        emit('DISMISSED', { step: index, stepId: current.step.id, total });
        finish();
      }}
    />
  );
}
